import React, { useCallback, useEffect, useState } from 'react'
import classNames from 'classnames'
import LazyHydrate from 'react-lazy-hydration'
import { Button, LinkButton } from '@/atoms/Button'
import CarouselNav from '@/atoms/CarouselNav'
import { GuildIconMono } from '@/atoms/Icons/GuildIconMono'
import { PlayCircleIcon } from '@/atoms/Icons/PlayCircleIcon'
import { Image } from '@/atoms/Image'
import { PlayButtonThumbnailOverlay } from '@/atoms/PlayButtonThumbnailOverlay'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { CaptionSM, HeadingXS, LabelSM, ParagraphLG, ParagraphSM, TitleLG } from '@/atoms/Text'
import { paths } from '@/constants'
import { VideoModal } from '@/molecules/VideoModal'
import { YoutubeVideoModal } from '@/molecules/YoutubeVideoModal'
import { GuildMembershipBanner } from '@/organisms/GuildMembershipBanner'
import { useGuildUser } from '@/services/GuildUserService'
import { Collection } from '@/types/codegen-contentful'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeAnalytics } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'
import useCarousel from '@/utils/useCarousel'
import { ProjectImpact, useGuildImpactStories, WatchFreeCard } from './data'

export interface GuildImpactViewProps {
  members: number
  guildMembershipNumbers: Collection
}

export const GuildImpactView: React.FC<GuildImpactViewProps> = ({ members, guildMembershipNumbers }) => {
  const [guildMembershipSectionVisited, setGuildMembershipSectionVisited] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const { isGuildMember } = useGuildUser()
  const { t } = useTranslate('guild')
  const welcomeUrl = 'https://content.angelstudios.com/guild/impact/Angel_101_Neal_Video_1_v4_16x9_2024-8-16_CJ.mp4'

  const votingBackgroundUrl = getCloudinaryImageUrl({
    path: '/v1722531475/angel-studios/guild/impact/hero_no_shadow.webp',
    transforms: 'q_auto:best,c_scale,w_1736,dpr_auto',
  })

  const guildMembers = members.toLocaleString()

  const impactProjects = useGuildImpactStories()

  const handleClose = () => {
    setShowModal(false)
  }

  useEffect(() => {
    switch (window?.location?.hash?.split('?')[0]) {
      case '#membership':
        if (guildMembershipSectionVisited) return
        setGuildMembershipSectionVisited(true)
        document.getElementById('membership')?.scrollIntoView({ behavior: 'smooth' })
        break
    }
  }, [guildMembershipSectionVisited])

  return (
    <main className="h-full w-full bg-core-gray-950 text-white">
      <section
        className="relative mb-20 flex min-h-full w-full flex-col items-center justify-center py-16 lg:min-h-screen lg:pt-20"
        id="hero"
        aria-labelledby="impact-hero-title"
      >
        <ResponsiveBackgroundImage
          src={votingBackgroundUrl}
          srcUrls={{
            xs: votingBackgroundUrl,
            sm: votingBackgroundUrl,
            md: votingBackgroundUrl,
            lg: votingBackgroundUrl,
            xl: votingBackgroundUrl,
            '2xl': votingBackgroundUrl,
            '3xl': votingBackgroundUrl,
          }}
          className="pointer-events-none mt-20 hidden touch-none select-none sm:hidden md:hidden lg:flex lg:bg-contain lg:bg-center"
        />
        <Image
          alt={'Guild Impact Top'}
          width={390}
          height={180}
          className="flex w-full lg:hidden"
          src="/v1723587992/angel-studios/guild/impact/impact-hero-mobile-top.webp"
        />
        <div className="relative flex max-w-[456px] flex-col gap-6 px-4 py-6 text-center lg:mt-20 xl:max-w-[648px] 2xl:max-w-[694px] se:py-4 3xl:max-w-[792px]">
          <HeadingXS
            weight="bold"
            className="lg:photon-heading-xs xl:photon-heading-md 2xl:photon-heading-lg"
            id="impact-hero-title"
          >
            {t('angelGuildChangingLives', 'Angel Guild members are changing lives and revolutionizing entertainment.')}
          </HeadingXS>
          <ParagraphSM
            color={'core-gray-400'}
            weight="medium"
            className="xl:photon-paragraph-md 2xl:photon-paragraph-lg 3xl:photon-paragraph-xl"
          >
            {t(
              'joinVisionariesBuildLibraryv3',
              `Join over {{ guildMembers }} visionaries choosing and funding entertainment that amplifies light.`,
              {
                guildMembers,
              },
            )}
          </ParagraphSM>
          <div className="mt-6 flex flex-row justify-center gap-6">
            <LinkButton
              variant={'white'}
              className="w-fit border-0 px-5 py-[11px] lg:max-h-12 xl:px-7 xl:py-[17px] 2xl:max-h-[56px]"
              href={isGuildMember ? `${paths.guild.index}#guild-activity` : paths.guild.join}
            >
              <LabelSM className="lg:photon-label-md 2xl:photon-label-lg">
                {isGuildMember ? t('viewMyImpact', 'View My Impact') : t('becomeMember', 'Become a Member')}
              </LabelSM>
            </LinkButton>
            <LinkButton
              variant={'gray-600'}
              className="w-fit border-0 !bg-[#434343] px-5 py-[11px] !text-white lg:max-h-12 xl:px-7 xl:py-[17px] 2xl:max-h-[56px]"
              href={paths.watch.index}
            >
              <LabelSM className="lg:photon-label-md 2xl:photon-label-lg">
                {isGuildMember ? t('watchNow', 'Watch Now') : t('watchForFree', 'Watch for Free')}
              </LabelSM>
            </LinkButton>
          </div>
        </div>
        <Image
          alt={'Guild Impact Top'}
          width={390}
          height={272}
          className="flex w-full lg:hidden"
          src="/v1723587992/angel-studios/guild/impact/impact-hero-mobile-top.webp"
        />
      </section>

      <section id="membership">
        <GuildMembershipBanner guildMembershipNumbers={guildMembershipNumbers} isGuildMember={isGuildMember} />
      </section>

      {impactProjects.map((project) => {
        return (
          <LazyHydrate whenVisible key={project.slug}>
            <ProjectImpactSection {...project} isGuildMember={isGuildMember} />
          </LazyHydrate>
        )
      })}

      <section className="lg:px-auto flex flex-col gap-12 px-4 py-12 md:px-8 lg:flex-row lg:items-center lg:justify-center lg:gap-[134px] lg:px-12 lg:py-48 xl:px-16 3xl:gap-[200px]">
        <div>
          <TitleLG
            weight="bold"
            className="md:photon-heading-xs lg:photon-heading-sm xl:photon-heading-md text-pretty text-left lg:max-w-[338px] xl:max-w-[418px] 2xl:max-w-[480px] 3xl:max-w-[542px]"
          >
            {t(
              'ourMissionAmplifyLightCeoExplainer',
              'At Angel Studios, our mission is to amplify light—but what exactly does that mean? Our CEO, Neal Harmon, explains our definition and how you can help us shine a light for the world to see',
            )}
            .
          </TitleLG>
        </div>
        <div className="relative max-w-[650px] cursor-pointer" onClick={() => setShowModal(true)}>
          <Image
            src={getCloudinaryImageUrl({
              path: '/v1727199971/angel-studios/guild/impact/Welcome_to_Angel.webp',
              transforms: 'q_auto:best',
            })}
            alt={t('nealWelcomeAngel', 'Neal Welcome to Angel')}
            height={601}
            width={1791}
            className="pointer-events-none rounded-3xl lg:min-h-[289px] lg:min-w-[538px]"
          />
          <PlayCircleIcon className="absolute left-1/2 top-1/2 z-10 h-12 w-12 -translate-x-1/2 -translate-y-1/2 rounded-full bg-core-gray-950/70" />
        </div>
      </section>
      <VideoModal showModal={showModal} videoUrl={welcomeUrl} handleClose={handleClose} showButton={false} autoplay />
    </main>
  )
}

const ImpactCard: React.FC<WatchFreeCard> = ({ author, description, imageUrl, videoUrl }) => {
  const [showStoryModal, setShowStoryModal] = useState(false)
  const { track } = useSafeAnalytics()

  const handleClose = () => {
    setShowStoryModal(false)
  }

  return (
    <div
      onClick={() => track('Guild Impact Card Clicked')}
      className="flex w-[208px] min-w-[208px] cursor-pointer snap-center flex-col justify-start py-8 text-white"
    >
      <div className="relative overflow-hidden rounded-2xl shadow-dark-3" onClick={() => setShowStoryModal(true)}>
        <Image
          className="flex max-h-[263px] min-h-[263px]"
          width={208}
          height={263}
          src={imageUrl ?? ''}
          alt={author}
        />
        <PlayButtonThumbnailOverlay />
      </div>
      <div className="mt-4 flex flex-col gap-1">
        <CaptionSM className="xl:photon-caption-md" color="core-gray-500" weight="medium">
          {`"${description}".`}
        </CaptionSM>
        <CaptionSM className="xl:photon-caption-md" color="core-gray-500" weight="medium">
          - {author}
        </CaptionSM>
      </div>
      <VideoModal
        showModal={showStoryModal}
        videoUrl={videoUrl}
        handleClose={handleClose}
        showButton={false}
        autoplay
      />
    </div>
  )
}

const ProjectImpactSection: React.FC<ProjectImpact> = ({
  slug,
  title,
  description,
  logoUrl,
  backgroundUrl,
  trailerUrl,
  stories,
  isGuildMember,
}) => {
  const [showTrailerModal, setShowTrailerModal] = useState(false)
  const [modalTrailerUrl, setTrailerUrl] = useState('')
  const [freeScrollerRef, { canScrollBack, canScrollForward, scrollBack, scrollForward }] =
    useCarousel<HTMLDivElement>()
  const { t } = useTranslate('guild')

  const handleTrailerClick = useCallback((trailerUrl: string) => {
    setShowTrailerModal(true)
    setTrailerUrl(trailerUrl)
  }, [])

  const handleModalClick = useCallback(() => {
    setShowTrailerModal(false)
    setTrailerUrl('')
  }, [])

  return (
    <section className="relative h-fit py-12 lg:py-0">
      <ResponsiveBackgroundImage
        src={backgroundUrl}
        srcUrls={{
          xs: backgroundUrl,
          sm: backgroundUrl,
          md: backgroundUrl,
          lg: backgroundUrl,
          xl: backgroundUrl,
          '2xl': backgroundUrl,
          '3xl': backgroundUrl,
        }}
        className="h-auto w-full !bg-contain lg:h-full lg:!bg-cover lg:!bg-top"
      />
      <div className="2xl:pl-18 relative flex flex-col items-start gap-6 pl-4 pr-10 md:pl-8 lg:py-20 lg:pl-12 xl:pl-16">
        <Image
          src={logoUrl}
          alt={title}
          height={40}
          width={119}
          className="pointer-events-none h-10 max-h-10 w-auto lg:h-[70px] lg:max-h-[70px]"
        />
        <div className="flex max-w-[400px] flex-col gap-4 lg:max-w-[577px] lg:gap-6">
          <HeadingXS weight="bold" className="lg:photon-heading-sm xl:photon-heading-md 2xl:photon-heading-xl">
            {title}
          </HeadingXS>
          <ParagraphSM color="core-gray-400" className="lg:photon-paragraph-md 2xl:photon-paragraph-lg">
            {description}
          </ParagraphSM>
        </div>
        <div className="flex flex-col gap-4 lg:flex-row">
          <LinkButton
            variant={'white'}
            className="w-fit gap-1 border-0 px-5 py-[11px] lg:max-h-12 lg:px-6 lg:py-[14px] xl:px-7 xl:py-[17px] 2xl:max-h-[56px]"
            href={isGuildMember ? `/watch/${slug}` : `/guild/join/${slug}`}
          >
            {!isGuildMember && <GuildIconMono size={24} />}
            <LabelSM className="lg:photon-label-md 2xl:photon-label-lg">
              {isGuildMember ? t('watchNow', 'Watch Now') : t('joinToWatch', 'Join To Watch')}
            </LabelSM>
          </LinkButton>
          <Button
            variant={'black'}
            onClick={() => handleTrailerClick(trailerUrl)}
            className="w-fit border-0 !bg-transparent py-[11px] pl-0 pr-5 lg:max-h-12 lg:py-[14px] lg:pr-6 xl:py-[17px] xl:pr-7 2xl:max-h-[56px]"
          >
            <LabelSM className="lg:photon-label-md 2xl:photon-label-lg">
              {t('watchTheTrailer', 'Watch The Trailer')}
            </LabelSM>
          </Button>
        </div>
      </div>
      <div className="relative mt-14">
        <ParagraphLG weight="bold" className="2xl:px-18 px-4 md:px-8 lg:px-12 xl:px-16">
          {t('realStories', 'Real stories')}:
        </ParagraphLG>
        <div className="relative w-full">
          <div
            ref={freeScrollerRef}
            className="relative flex w-full snap-x snap-mandatory flex-row gap-8 overflow-scroll px-4 scrollbar-none sm:flex-row md:mb-[118px] md:px-8 lg:px-12 xl:px-16"
          >
            {stories.map((data: WatchFreeCard) => {
              return <ImpactCard key={data.description} {...data} />
            })}
          </div>
          <CarouselNav
            onClick={scrollBack}
            active={canScrollBack}
            orientation="horizontal"
            direction="back"
            className={classNames('ml-4', { 'opacity-75 hover:opacity-100': canScrollBack })}
            theme="dark"
          />
          <CarouselNav
            onClick={scrollForward}
            active={canScrollForward}
            orientation="horizontal"
            direction="forward"
            className={classNames('mr-4', { 'opacity-75 hover:opacity-100': canScrollForward })}
            theme="dark"
          />
        </div>
      </div>
      <YoutubeVideoModal youTubeUrl={modalTrailerUrl} handleClose={handleModalClick} showModal={showTrailerModal} />
    </section>
  )
}
